import Component from '@glimmer/component';
import style from 'ember-style-modifier/modifiers/style';

interface StickyLayoutSignature {
  Args: {
    /**
     * Width of the sticky sidebar (e.g., 'w-16', 'w-64')
     * @default 'w-16'
     */
    sidebarWidth?: string;
    /**
     * Custom height calculation for the sticky elements
     * @default 'calc(100vh - 73px)'
     */
    stickyHeight?: string;
    /**
     * Whether the sidebar should be sticky
     * @default true
     */
    sidebarSticky?: boolean;
  };
  Element: HTMLDivElement;
  Blocks: {
    header?: [];
    main: [];
    sidebar: [];
    footer?: [];
  };
}

/**
 * StickyLayout Component
 *
 * A reusable layout component that provides:
 * - Sticky right sidebar with configurable width (sticky by default)
 * - Optional sticky header (when header block is provided)
 * - Optional sticky footer (when footer block is provided)
 * - Scrollable main content area
 *
 * Usage:
 * ```glimmer-ts
 * <StickyLayout @sidebarWidth="w-16" @sidebarSticky={{false}}>
 *   <:header>Header content</:header>
 *   <:main>Main content</:main>
 *   <:sidebar>Sidebar content</:sidebar>
 *   <:footer>Footer content</:footer>
 * </StickyLayout>
 * ```
 */
export default class StickyLayout extends Component<StickyLayoutSignature> {
  get sidebarWidth() {
    return this.args.sidebarWidth || 'w-16';
  }

  get stickyHeight() {
    return this.args.stickyHeight || 'calc(100vh - 73px)';
  }

  get sidebarSticky() {
    return this.args.sidebarSticky ?? true;
  }

  get sidebarClasses() {
    const baseClasses = `${this.sidebarWidth} tio-bg-surface border-l tio-border-default flex flex-col items-center py-4 gap-4`;

    if (this.sidebarSticky) {
      return `${baseClasses} sticky top-0`;
    }

    return baseClasses;
  }

  <template>
    <div class="tio-col-flush min-h-screen" data-sticky-layout ...attributes>
      {{! Optional Header }}
      {{#if (has-block "header")}}
        <div class="sticky top-0 tio-bg-surface border-b tio-border-default" data-sticky-header>
          {{yield to="header"}}
        </div>
      {{/if}}

      {{! Main Content Container }}
      <div class="flex-1 flex min-h-0 min-w-0" data-layout-body>
        {{! Scrollable Main Content }}
        <div class="tio-fill-min overflow-y-auto" data-main-content>
          {{yield to="main"}}
        </div>

        {{! Sticky Right Sidebar }}
        {{#if this.sidebarSticky}}
          <div
            class="{{this.sidebarClasses}}"
            {{style height=this.stickyHeight}}
            data-sticky-sidebar
          >
            {{yield to="sidebar"}}
          </div>
        {{else}}
          <div class="{{this.sidebarClasses}}" data-sticky-sidebar>
            {{yield to="sidebar"}}
          </div>
        {{/if}}
      </div>

      {{! Optional Sticky Footer }}
      {{#if (has-block "footer")}}
        <div
          class="tio-row-end gap-4 sticky bottom-0 tio-bg-surface border-t tio-border-default px-6 py-4"
          data-sticky-footer
        >
          {{yield to="footer"}}
        </div>
      {{/if}}
    </div>
  </template>
}
